import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Shared-rig contract (see the framing addendum): the light here is the
// buffer's own history; a rig would only pollute the loop.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'tessel',
  family: 'shader',
  danceability: 0.80,
  // 1, as the brief asks. Same consequence as kaleido.js: the radio page
  // measures tier 0 on its real hardware, so Tessel is not OFFERED there. The
  // frame-time governor can still put it at tier 0 once selected elsewhere,
  // which is why TIER[0] is a real layout and not a hole.
  requiresTier: 1,
  energyLevel: 0.76,
  bpmMin: 100,
  bpmMax: 170,
  movement: 0.75,
  tonality: 0.70,
  prefers: { gridStrength: [0.50, 1.0] },
  enabled: true
};

// ============================================================================
// Tessel: Kaleido's feedback loop with the fold changed from RADIAL to a
// WALLPAPER tiling. Everything the loop is made of is Echo's and unchanged -
// the FeedbackBuffer, the three cumulative stability guards, the luminance
// governor, the masked dither, the track fade - and everything about how the
// symmetry accumulates is Kaleido's. Only the fold is new.
//
// What changes because the symmetry is periodic rather than radial:
//   - it fills the frame, so there is no vignette and no centre
//   - there is no singularity to special-case
//   - the pattern can be TRANSLATED. Sliding the whole tiling a quarter cell
//     on the bar is the one gesture a kaleidoscope cannot make, and it is what
//     this piece is for.
//
// THE FOLD IS THE WHOLE FILE, and two of the five groups do not fold the way
// they look like they should:
//
//   pmm, cmm, p4m fold by reflecting each axis - abs(fract(x) - 0.5) - which
//   is continuous by construction. cmm is that same fold in a sheared frame,
//   which is what a rhombic lattice with mirrors on its diagonals IS.
//
//   p6m and p3m1 are folded by finding the NEAREST LATTICE POINT of a
//   triangular lattice and then applying Kaleido's radial fold about it. That
//   is seamless only if the Voronoi edges between lattice points are
//   themselves mirror lines of the fold, and whether they are depends on the
//   PHASE of the fold's mirrors. Measured, by walking dense lines across the
//   plane and looking for a jump in the folded position:
//
//     order 6, mirrors at  0 deg -> largest jump 0.00067 (= the sample step)
//     order 6, mirrors at 30 deg -> largest jump 0.00067
//     order 3, mirrors at  0 deg -> largest jump 0.49967   <-- a seam
//     order 3, mirrors at 30 deg -> largest jump 0.00067
//
//   So the order-3 group that tiles without a seam is the one whose mirrors
//   sit at 30 degrees to the lattice directions. That group is p31m, not
//   p3m1: they are sisters, both 3-fold with mirrors, differing only in how
//   the mirrors are oriented against the lattice. The brief asks for p3m1 and
//   ?tesselgroup=p3m1 selects this, because p3m1 folded the obvious way
//   produces exactly the seam the brief spends a paragraph warning about.
//
// ?tesselseam=1 draws the fold lines over the output. Nothing in the image may
// line up with them. ?tesseljoints=0 removes the decorative grout, which is
// drawn on those same lines and would otherwise be indistinguishable from a
// seam to any measurement.
// ============================================================================

// --- the loop (Echo's numbers, unchanged) -----------------------------------
const DECAY_DEFAULT = 0.955;
const DECAY_MIN = 0.85;
const DECAY_MAX = 0.975;
const FLOOR_SUB = 0.004;
const CEILING = 0.92;
const GOVERN_EVERY_FRAMES = 30;
const GOVERN_HIGH = 0.35;
const GOVERN_RESUME = 0.25;
const GOVERN_LOW = 0.05;
const GOVERN_STEP = 0.01;
const LUM_SIZE = 8;
const TRACK_FADE_S = 1.2;
const FADE_DECAY = 0.96;
const BAND_TAU = 0.15;

// --- the groups ---------------------------------------------------------------
// Only mirror groups, because a reflection is what guarantees that two pixels
// either side of a cell boundary read the same value - the same reason
// Kaleido's fold is an abs() and not a rotation.
const GROUPS = ['pmm', 'p4m', 'p6m', 'p3m1', 'cmm'];
const GROUP_ID = { pmm: 0, p4m: 1, p6m: 2, p3m1: 3, cmm: 4 };
// Opens and closes on the two that read as "a tiling"; the middle of the track
// gets everything the tier can do.
const CALM_GROUPS = ['pmm', 'p4m'];
// Cells across the width. 2-3 keeps the cover legible in each, 6-8 is the
// wallpaper register, 12 is lace.
const CELL_COUNTS = [2, 3, 4, 6, 8, 12];
const DROP_CELLS = 1;

// --- the transforms -------------------------------------------------------------
const CELL_BREATH = 0.04;          // +-4% with beatPhase
const CELL_BREATH_DECAY = 4.5;
// BELOW 1, so each cell's own centre is a SOURCE and matter flows outward to
// the mirror walls, which fold it back in - the fold is an isometry onto the
// fundamental domain, so nothing is lost and the ornament grows. Above 1 the
// flow runs the other way: everything drains into the cell centre, where the
// vertex attenuator and the floor grind it to black, and the frame is a grid
// of dim dots. Measured mean frame luminance 0.007 at 1.015.
const ZOOM_PER_CELL = 0.988;       // the local Droste nesting, outward
const SLIDE_PER_BAR = 0.25;        // a quarter cell
const SLIDE_GLIDE_BEATS = 1;
// The slide's direction turns by the group's own angle, so the motion always
// runs along a lattice direction rather than across the mirrors.
const SLIDE_TURN = { pmm: 90, p4m: 90, cmm: 90, p6m: 60, p3m1: 60 };
const ROT_STEP_DEG = { pmm: 90, p4m: 45, cmm: 90, p6m: 30, p3m1: 60 };

// --- hue --------------------------------------------------------------------------
// Because the tiling SLIDES, the hue gradient lays down diagonal bands across
// the frame rather than Kaleido's concentric rings.
const HUE_SHIFT = 0.005;

// --- injection ------------------------------------------------------------------
// Of the CELL, not the frame: the cover has to fill the one cell whose content
// is real. It can be larger than the cell, because the injection is itself
// folded - anything outside the fundamental domain is reflected back into it
// rather than lost, so the whole cover contributes.
const ART_SIZE = 1.15;
const ART_OPACITY = 0.08;
const ART_RESEED_OPACITY = 0.40;
const RESEED_LIFE_S = 0.05;
const SHAPE_LIFE_S = 0.12;
// What the last shape is held at between beats when there is no cover, so the
// loop always has a source. Faint enough that it reads as an afterglow.
const SHAPE_HOLD_OPACITY = 0.10;

// --- the seams ------------------------------------------------------------------
const VERTEX_ATTEN = 0.94;
const VERTEX_ATTEN_R = 0.06;       // in cell units
const JOINT_DARKEN = 0.15;         // the mirrored tiling's own grout

const TIER = {
  0: { scale: 0.25, groups: ['pmm', 'p4m', 'p6m'], joints: false, soft: false },
  1: { scale: 0.25, groups: ['pmm', 'p4m', 'p6m'], joints: false, soft: false },
  2: { scale: 0.5, groups: GROUPS, joints: true, soft: true }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

function hueMatrix(angle, out) {
  const c = Math.cos(angle), s = Math.sin(angle);
  const a = 1 / 3, b = Math.sqrt(1 / 3);
  out.set(
    c + (1 - c) * a, a * (1 - c) - b * s, a * (1 - c) + b * s,
    a * (1 - c) + b * s, c + (1 - c) * a, a * (1 - c) - b * s,
    a * (1 - c) - b * s, a * (1 - c) + b * s, c + (1 - c) * a
  );
  return out;
}

const fullscreenVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// ============================================================================
// The feedback pass.
// ============================================================================
function feedbackFragmentShader(soft) {
  return `
  precision highp float;

  uniform sampler2D uPrevious;
  uniform float uCells;
  uniform vec2 uOffset;        // the slide, in cells
  uniform float uZoom;         // per-cell, about each cell's own centre
  uniform float uRotation;
  uniform int uGroup;
  uniform float uSoft;         // one buffer pixel, in cell units
  uniform float uAspect;
  uniform sampler2D uSource;   // the injection, tiled with everything else
  uniform float uDecay;
  uniform mat3 uHueMat;
  uniform float uSeed;

  varying vec2 vUv;

  const float PI = 3.141592653589793;
  const float SQ3 = 1.7320508075688772;

  // Kaleido's C1 abs(): matches abs() outside +-e, and inside it is the
  // parabola whose slope reaches +-1 exactly at the join. A derivative
  // discontinuity on a fold line is re-read and re-sharpened by the loop every
  // frame until it is a crease, so C0 is not enough.
  float softAbs(float x, float e) {
    float a = abs(x);
    return a >= e ? a : (x * x / (2.0 * e) + e * 0.5);
  }

  // One axis of a rectangular fold: [0,1) -> [0,0.5], with BOTH of its corners
  // rounded - the one at the cell's centre and the one at its edge.
  float fold1(float u, float e) {
    float t = fract(u) - 0.5;
    ${soft ? `
    float f = softAbs(t, e);
    return 0.5 - softAbs(0.5 - f, e);` : `
    return abs(t);`}
  }

  // Kaleido's angular fold, reused verbatim for the two hexagonal groups.
  float foldTri(float u, float e) {
    float t = 2.0 * u - 1.0;
    ${soft ? `
    float f = softAbs(t, e);
    return 1.0 - softAbs(1.0 - f, e);` : `
    return abs(t);`}
  }

  // Nearest point of a triangular lattice, by the standard cube-coordinate
  // rounding: rounding axial coordinates independently lands on the wrong
  // point near the cell edges, which would put a seam on every one of them.
  vec2 hexRound(vec2 ax) {
    float x = ax.x, z = ax.y, y = -x - z;
    float rx = floor(x + 0.5), ry = floor(y + 0.5), rz = floor(z + 0.5);
    float dx = abs(rx - x), dy = abs(ry - y), dz = abs(rz - z);
    if (dx > dy && dx > dz) rx = -ry - rz;
    else if (dy > dz) ry = -rx - rz;
    else rz = -rx - ry;
    return vec2(rx, rz);
  }

  // Returns the folded position IN CELL UNITS, relative to the cell's own
  // centre, and writes the distance to the nearest fixed point of the fold
  // into vd.
  //
  // IN CELL UNITS is the whole point. The first version normalised the
  // fundamental domain to [0,1] and sampled the previous frame across its full
  // extent - which means every cell read the WHOLE frame, so content shrank by
  // the cell count on every pass and degenerated into sub-pixel traces within
  // a second. Measured: the injected bar survived as a thin outline and the
  // frame mean sat at 0.004. A symmetry is an ISOMETRY; the fold has to return
  // the plane to itself at the same scale, and the only thing allowed to
  // change scale is uZoom.
  vec2 foldPoint(vec2 c, out float vd) {
    float e = uSoft;
    if (uGroup == 2 || uGroup == 3) {
      // p6m / p3m1(p31m): nearest lattice point, then Kaleido's radial fold.
      float order = uGroup == 2 ? 6.0 : 3.0;
      // 30 degrees for order 3 - see the header. At phase 0 the fold jumps by
      // half a cell across every Voronoi edge.
      float phase = uGroup == 2 ? 0.0 : PI / 6.0;
      vec2 ax = vec2(c.x - c.y / SQ3, c.y * 2.0 / SQ3);
      vec2 ra = hexRound(ax);
      vec2 lp = vec2(ra.x + ra.y * 0.5, ra.y * SQ3 * 0.5);
      vec2 rel = c - lp;
      float r = length(rel);
      vd = r;
      float sector = 2.0 * PI / order;
      float u = fract((atan(rel.y, rel.x) - phase) / sector);
      float a = foldTri(u, e / max(r, 1e-4) / (sector * 0.5)) * sector * 0.5 + phase;
      return r * uZoom * vec2(cos(a), sin(a));
    }
    if (uGroup == 4) {
      // cmm: the same rectangular fold in a frame turned 45 degrees, which is
      // a centred (rhombic) lattice with mirrors on its diagonals.
      //
      // The two mirror families have normals (1, k) and (1, -k), and those are
      // perpendicular ONLY at k = 1. The first version used k = 0.62 to make
      // the lattice visibly rhombic, which SHEARS the fold instead of rotating
      // it: conjugating a reflection group by a shear gives skew involutions,
      // not Euclidean mirrors. The tiling stayed seamless - measured 1.6x the
      // sampling step, so continuous - but it was a sheared pmm wearing cmm's
      // name, and reflecting the rendered frame about its own mirror lines
      // disagreed with itself by 0.65 where every other group managed 0.00.
      // At k = 1 both reflections are exact to the last bit.
      vec2 d = vec2(c.x + c.y, c.x - c.y);
      vd = length(d - vec2(floor(d.x + 0.5), floor(d.y + 0.5)));
      // Back out of the sheared frame, so the result is a real displacement.
      vec2 f = vec2(fold1(d.x, e), fold1(d.y, e)) * uZoom;
      return vec2(f.x + f.y, f.x - f.y) * 0.5;
    }
    vec2 f = vec2(fold1(c.x, e), fold1(c.y, e));
    vd = length(c - vec2(floor(c.x + 0.5), floor(c.y + 0.5)));
    if (uGroup == 1) {
      // p4m: the extra mirror on the diagonal, blended across it so the swap
      // does not put a crease of its own where the other two meet.
      float m = smoothstep(-e, e, f.x - f.y);
      f = mix(f.yx, f.xy, m);
    }
    return f * uZoom;
  }

  void main() {
    // Aspect-corrected so the cells are square whatever the buffer is. The
    // fold's domain is then square and the frame is not, so mapping the domain
    // back on to the frame stretches it by the aspect - a fixed anamorphosis,
    // identical every frame, which is what fills the corners.
    vec2 q = vec2(vUv.x * uAspect, vUv.y);
    float cr = cos(uRotation), sr = sin(uRotation);
    vec2 qc = q - vec2(uAspect, 1.0) * 0.5;
    qc = vec2(qc.x * cr - qc.y * sr, qc.x * sr + qc.y * cr);
    vec2 c = (qc * uCells / uAspect) + uOffset;

    float vd;
    vec2 f = foldPoint(c, vd);
    // The folded point put back into the plane, in the CANONICAL cell at the
    // middle of the frame. That cell is the only one whose content is real;
    // every other cell on screen is its mirror, which is what a tiling is.
    // Putting it at the centre rather than at a corner is what lets the cover
    // and the beat shapes, which are injected around the middle, be the matter
    // that gets replicated.
    vec2 qs = vec2(uAspect, 1.0) * 0.5 + f * (uAspect / uCells);
    vec2 suv = clamp(vec2(qs.x / uAspect, qs.y), 0.0, 1.0);
    vec3 prev = texture2D(uPrevious, suv).rgb;

    prev = uHueMat * prev;

    // The VERTICES of a tiling are fixed points of several reflections at once
    // and concentrate energy exactly as Kaleido's centre does - with the
    // difference that there are dozens of them. Same remedy, applied at every
    // one: decay a little harder close in.
    prev *= mix(${VERTEX_ATTEN.toFixed(3)}, 1.0, smoothstep(0.0, ${VERTEX_ATTEN_R.toFixed(3)}, vd));

    // STABILITY - Echo's three guards, cumulative and unchanged.
    prev *= uDecay;
    prev -= vec3(${FLOOR_SUB.toFixed(4)});
    prev = max(prev, 0.0);

    // THE INJECTION, sampled at the FOLDED coordinate, inside the loop - see
    // the same note in kaleido.js. Drawn over the top of the fold instead, the
    // cover stays one hard-edged square in the middle of the frame, belonging
    // to no cell, while every other cell has no source at all and FLOOR_SUB
    // empties it. Folded, the cover IS the cell's content and the tiling
    // replicates it, which is the whole point of the visualizer.
    prev += texture2D(uSource, suv).rgb;
    prev = min(prev, vec3(${CEILING.toFixed(3)}));

    // Echo's masked dither, unchanged.
    vec2 fcw = mod(floor(gl_FragCoord.xy) + uSeed, 64.0);
    float dith = (fract(dot(fcw, vec2(0.7548776662, 0.5698402909))) - 0.5) / 255.0;
    float sig = step(3.5 / 255.0, max(prev.r, max(prev.g, prev.b)));

    gl_FragColor = vec4(prev + dith * sig, 1.0);
  }
`;
}

// ============================================================================
// The display pass. Full frame, no vignette: the tiling covers the plane and
// the corners are part of it.
// ============================================================================
function displayFragmentShader(joints) {
  return `
  precision highp float;
  uniform sampler2D uBuffer;
  uniform float uCells;
  uniform vec2 uOffset;
  uniform float uRotation;
  uniform int uGroup;
  uniform float uAspect;
  uniform float uPx;
  uniform float uSeamDebug;
  varying vec2 vUv;
  const float PI = 3.141592653589793;
  const float SQ3 = 1.7320508075688772;

  // Distance to the nearest fold line, in cell units. Only the display uses
  // this - the feedback pass must never see it, or the grout would be fed back
  // and compound into a hard lattice of bars.
  float foldLineDistance(vec2 c) {
    if (uGroup == 2 || uGroup == 3) {
      float order = uGroup == 2 ? 6.0 : 3.0;
      float phase = uGroup == 2 ? 0.0 : PI / 6.0;
      vec2 ax = vec2(c.x - c.y / SQ3, c.y * 2.0 / SQ3);
      float x = ax.x, z = ax.y, y = -x - z;
      float rx = floor(x + 0.5), ry = floor(y + 0.5), rz = floor(z + 0.5);
      float dx = abs(rx - x), dy = abs(ry - y), dz = abs(rz - z);
      if (dx > dy && dx > dz) rx = -ry - rz; else if (dy > dz) ry = -rx - rz; else rz = -rx - ry;
      vec2 lp = vec2(rx + rz * 0.5, rz * SQ3 * 0.5);
      vec2 rel = c - lp;
      float r = length(rel);
      float sector = 2.0 * PI / order;
      float u = fract((atan(rel.y, rel.x) - phase) / sector);
      return min(u, 1.0 - u) * sector * r;
    }
    vec2 d = uGroup == 4 ? vec2(c.x + c.y, c.x - c.y) : c;
    vec2 t = abs(fract(d) - 0.5);
    float m = min(0.5 - t.x, 0.5 - t.y);
    if (uGroup == 1) m = min(m, abs(t.x - t.y) * 0.7071);
    return m;
  }

  void main() {
    vec3 col = texture2D(uBuffer, vUv).rgb;
    vec2 q = vec2(vUv.x * uAspect, vUv.y);
    float cr = cos(uRotation), sr = sin(uRotation);
    vec2 qc = q - vec2(uAspect, 1.0) * 0.5;
    qc = vec2(qc.x * cr - qc.y * sr, qc.x * sr + qc.y * cr);
    vec2 c = (qc * uCells / uAspect) + uOffset;
    float d = foldLineDistance(c);
    // One buffer pixel, expressed in cell units.
    float w = uPx * uCells / uAspect;

    ${joints ? `
    // The grout of a mirrored tiling: one pixel at 15%. It makes the structure
    // legible without breaking the continuity the fold works so hard for,
    // because it is drawn HERE and never fed back.
    col *= 1.0 - ${JOINT_DARKEN.toFixed(3)} * (1.0 - smoothstep(0.0, w, d));` : ''}

    if (uSeamDebug > 0.5) {
      col = mix(col, vec3(1.0, 0.0, 0.0), (1.0 - smoothstep(0.0, w * 1.5, d)) * 0.85);
    }
    gl_FragColor = vec4(col, 1.0);
  }
`;
}

const shapeVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const barFragmentShader = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform vec2 uHalfSize;
  uniform vec2 uRect;
  uniform float uPx;
  varying vec2 vUv;
  void main() {
    vec2 p = abs((vUv - 0.5) * 2.0 * uHalfSize);
    float w = max(uPx * 1.5, 1e-5);
    float a = clamp((uRect.x - p.x) / w, 0.0, 1.0) * clamp((uRect.y - p.y) / w, 0.0, 1.0);
    gl_FragColor = vec4(uColor * (a * uOpacity), 1.0);
  }
`;

const discFragmentShader = `
  precision mediump float;
  uniform vec3 uColor;
  uniform float uOpacity;
  uniform vec2 uHalfSize;
  uniform float uInner;
  uniform float uOuter;
  uniform float uPx;
  varying vec2 vUv;
  void main() {
    float r = length((vUv - 0.5) * 2.0 * uHalfSize);
    float w = max(uPx * 1.5, 1e-5);
    float a = clamp((uOuter - r) / w, 0.0, 1.0) * clamp((r - uInner) / w, 0.0, 1.0);
    gl_FragColor = vec4(uColor * (a * uOpacity), 1.0);
  }
`;

export class TesselVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    const g = urlParam('tesselgroup');
    this._forcedGroup = GROUPS.indexOf(g) !== -1 ? g : null;
    const cells = parseInt(urlParam('tesselcells'), 10);
    this._forcedCells = Number.isFinite(cells) && cells >= 1 && cells <= 24 ? cells : null;
    const d = parseFloat(urlParam('tesseldecay'));
    this._forcedDecay = Number.isFinite(d) ? d : null;
    // The grout is drawn ON the fold lines, so a seam metric cannot tell it
    // apart from a real discontinuity. ?tesseljoints=0 takes it off to measure
    // the fold itself.
    this._noJoints = urlParam('tesseljoints') === '0';
    this._seamDebug = urlParam('tesselseam') === '1';
    this._lumDebug = urlParam('tessellum') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    const layout = TIER[this._tier];
    this._layout = layout;
    this._buffer = new FeedbackBuffer({ scale: layout.scale });
    // The injection gets its own target so the feedback pass can sample it at
    // the folded coordinate. Same scale as the buffer, one target, no history.
    this._source = new FeedbackBuffer({ scale: layout.scale, single: true });

    this._group = this._forcedGroup || 'p4m';
    this._cells = this._forcedCells || 4;

    this._feedbackUniforms = {
      uPrevious: { value: null },
      uCells: { value: this._cells },
      uOffset: { value: new THREE.Vector2(0, 0) },
      uZoom: { value: ZOOM_PER_CELL },
      uRotation: { value: 0 },
      uGroup: { value: GROUP_ID[this._group] },
      uSoft: { value: 0.004 },
      uSource: { value: null },
      uAspect: { value: 16 / 9 },
      uDecay: { value: this._forcedDecay != null ? this._forcedDecay : DECAY_DEFAULT },
      uHueMat: { value: hueMatrix(0, new THREE.Matrix3()) },
      uSeed: { value: 0 }
    };
    this._feedbackScene = new THREE.Scene();
    this._feedbackQuad = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._feedbackUniforms,
        vertexShader: fullscreenVertexShader,
        fragmentShader: feedbackFragmentShader(layout.soft),
        depthTest: false, depthWrite: false
      })
    );
    this._feedbackQuad.frustumCulled = false;
    this._feedbackScene.add(this._feedbackQuad);

    // --- injection ---
    this._injectScene = new THREE.Scene();
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;

    this._artTexture = null;
    this._artMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({
        transparent: true, opacity: ART_OPACITY,
        blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false
      })
    );
    this._artMesh.scale.set(ART_SIZE, ART_SIZE, 1);   // rescaled per frame to the cell
    this._artMesh.visible = false;
    this._injectScene.add(this._artMesh);

    this._shapeMaterials = [];
    this._bar = this._makeBar(0.13, 0.011);
    this._ring = this._makeDisc(0.085, 0.105);
    this._dot = this._makeDisc(0.0, 0.045);
    this._shapes = [this._bar, this._ring, this._dot];
    for (const m of this._shapes) { m.visible = false; this._injectScene.add(m); }

    // --- governor (Echo's, unchanged) ---
    this._lumTarget = new THREE.WebGLRenderTarget(LUM_SIZE, LUM_SIZE, {
      minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
      depthBuffer: false, stencilBuffer: false
    });
    this._lumScene = new THREE.Scene();
    this._lumUniforms = { uBuffer: { value: null } };
    this._lumQuad = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._lumUniforms,
        vertexShader: fullscreenVertexShader,
        fragmentShader: 'precision mediump float;\nuniform sampler2D uBuffer;\nvarying vec2 vUv;\n'
          + 'void main() { gl_FragColor = vec4(texture2D(uBuffer, vUv).rgb, 1.0); }',
        depthTest: false, depthWrite: false
      })
    );
    this._lumQuad.frustumCulled = false;
    this._lumScene.add(this._lumQuad);
    this._lumPixels = new Uint8Array(LUM_SIZE * LUM_SIZE * 4);

    // --- display ---
    this._displayUniforms = {
      uBuffer: { value: null },
      uCells: this._feedbackUniforms.uCells,
      uOffset: this._feedbackUniforms.uOffset,
      uRotation: this._feedbackUniforms.uRotation,
      uGroup: this._feedbackUniforms.uGroup,
      uAspect: { value: 16 / 9 },
      uPx: { value: 1 / 360 },
      uSeamDebug: { value: this._seamDebug ? 1 : 0 }
    };
    this._displayMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.ShaderMaterial({
        uniforms: this._displayUniforms,
        vertexShader: fullscreenVertexShader,
        fragmentShader: displayFragmentShader(layout.joints && !this._noJoints),
        depthTest: false, depthWrite: false
      })
    );
    this._displayMesh.frustumCulled = false;
    this._displayMesh.renderOrder = -1000;
    const self = this;
    this._displayMesh.onBeforeRender = function (renderer) { self._runPasses(renderer); };
    scene.add(this._displayMesh);

    // --- state ---
    this._time = 0;
    this._paused = false;
    this._decay = this._forcedDecay != null ? this._forcedDecay : DECAY_DEFAULT;
    // DECAY_MAX, where Echo uses DECAY_DEFAULT. Echo caps the governor's
    // upward travel at the decay it started from, so the governor can only
    // ever reduce - which is right for a near-identity transform that always
    // has plenty of gain. A FOLD discards all but 1/order of the plane every
    // pass, and how much it discards depends on the group: measured over the
    // same 31 s, pmm settled at frame mean 0.17 and p3m1 at 0.029, below the
    // governor's own GOVERN_LOW. Letting it climb to DECAY_MAX is what the
    // governor is for, and 0.975 is still below 1, still under the floor
    // subtraction and still under the hard ceiling.
    this._decayCeiling = Math.max(this._decay, DECAY_MAX);
    this._governorHot = false;
    this._frameCount = 0;
    this._lastLum = 0;
    this._rotation = 0;
    this._slideDir = 0;
    this._offFrom = new THREE.Vector2(0, 0);
    this._offTo = new THREE.Vector2(0, 0);
    this._offT = 1;
    this._offDur = 0.5;
    this._lastPhraseIndex = null;
    this._lastBeatIndex = null;
    this._lastBarIndex = null;
    this._lowSmooth = 0.5;
    this._midSmooth = 0.5;
    this._highSmooth = 0.5;
    this._hueDir = 1;
    this._palette = [new THREE.Color(0x39a0ff), new THREE.Color(0xff3ba7), new THREE.Color(0xffd23b)];
    this._lastPaletteRef = undefined;
    this._lastTrackId = undefined;
    this._lastArtworkUrl = undefined;
    this._coldStart = true;
    this._nextSeedAt = 0;
    this._fadeUntil = -1;
    this._fading = false;
    this._shapeUntil = -1;
    this._reseedUntil = -1;
    this._dropUntil = -1;
    this._cellsBefore = null;
    this._bpm = null;
    this._beatPhase = 0;

    if (this._lumDebug) {
      const el = document.createElement('div');
      el.style.cssText = 'position:fixed;left:10px;top:10px;z-index:99999;'
        + 'font:12px/1.5 ui-monospace,monospace;color:#9fe;background:rgba(0,0,0,.65);'
        + 'padding:6px 9px;border-radius:6px;white-space:pre';
      document.body.appendChild(el);
      this._lumEl = el;
    }
  }

  _makeBar(halfX, halfY) {
    const m = 0.02;
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(0xffffff) }, uOpacity: { value: 1 },
        uHalfSize: { value: new THREE.Vector2(halfX + m, halfY + m) },
        uRect: { value: new THREE.Vector2(halfX, halfY) }, uPx: { value: 1 / 360 }
      },
      vertexShader: shapeVertexShader, fragmentShader: barFragmentShader,
      blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false
    });
    this._shapeMaterials.push(mat);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry((halfX + m) * 2, (halfY + m) * 2), mat);
    mesh.frustumCulled = false;
    return mesh;
  }

  _makeDisc(inner, outer) {
    const half = outer * 1.2;
    const mat = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(0xffffff) }, uOpacity: { value: 1 },
        uHalfSize: { value: new THREE.Vector2(half, half) },
        uInner: { value: inner }, uOuter: { value: outer }, uPx: { value: 1 / 360 }
      },
      vertexShader: shapeVertexShader, fragmentShader: discFragmentShader,
      blending: THREE.AdditiveBlending, transparent: true, depthTest: false, depthWrite: false
    });
    this._shapeMaterials.push(mat);
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(half * 2, half * 2), mat);
    mesh.frustumCulled = false;
    return mesh;
  }

  // Echo's governor, unchanged apart from the log prefix.
  _measureLuminance(renderer) {
    this._lumUniforms.uBuffer.value = this._buffer.write.texture;
    renderer.setRenderTarget(this._lumTarget);
    renderer.render(this._lumScene, this._ortho);
    renderer.readRenderTargetPixels(this._lumTarget, 0, 0, LUM_SIZE, LUM_SIZE, this._lumPixels);
    let sum = 0;
    for (let i = 0; i < this._lumPixels.length; i += 4) {
      sum += 0.299 * this._lumPixels[i] + 0.587 * this._lumPixels[i + 1] + 0.114 * this._lumPixels[i + 2];
    }
    const lum = sum / (255 * LUM_SIZE * LUM_SIZE);
    this._lastLum = lum;
    if (lum > GOVERN_HIGH) this._governorHot = true;
    if (this._governorHot) {
      if (lum > GOVERN_RESUME) {
        const before = this._decay;
        this._decay = Math.max(DECAY_MIN, this._decay - GOVERN_STEP);
        if (this._decay !== before) {
          // eslint-disable-next-line no-console
          console.log('[tessel] governor: mean luminance ' + lum.toFixed(3)
            + ' -> decay ' + before.toFixed(3) + ' -> ' + this._decay.toFixed(3));
        }
      } else {
        this._governorHot = false;
      }
    } else if (lum < GOVERN_LOW && this._decay < Math.min(this._decayCeiling, DECAY_MAX)) {
      const before = this._decay;
      this._decay = Math.min(Math.min(this._decayCeiling, DECAY_MAX), this._decay + GOVERN_STEP);
      // eslint-disable-next-line no-console
      console.log('[tessel] governor: mean luminance ' + lum.toFixed(3)
        + ' -> decay ' + before.toFixed(3) + ' -> ' + this._decay.toFixed(3));
    }
  }

  _runPasses(renderer) {
    const rebuilt = this._buffer.ensureSize(renderer);
    this._source.ensureSize(renderer);
    if (rebuilt) {
      this._displayUniforms.uBuffer.value = this._buffer.read.texture;
      const aspect = this._buffer.width / Math.max(1, this._buffer.height);
      this._ortho.left = -0.5 * aspect;
      this._ortho.right = 0.5 * aspect;
      this._ortho.updateProjectionMatrix();
      this._feedbackUniforms.uAspect.value = aspect;
      this._displayUniforms.uAspect.value = aspect;
      const px = 1 / Math.max(1, this._buffer.height);
      this._displayUniforms.uPx.value = px;
      for (const mat of this._shapeMaterials) mat.uniforms.uPx.value = px;
      this._updateSoft();
    }
    if (this._paused) {
      this._displayUniforms.uBuffer.value = this._buffer.read.texture;
      return;
    }
    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;

    // The injection first, into its own target: the feedback pass reads it at
    // the folded coordinate and is the only thing that writes the buffer.
    renderer.setRenderTarget(this._source.write);
    renderer.autoClear = false;
    renderer.clear(true, false, false);
    renderer.render(this._injectScene, this._ortho);

    this._feedbackUniforms.uPrevious.value = this._buffer.read.texture;
    this._feedbackUniforms.uSource.value = this._source.read.texture;
    this._feedbackUniforms.uDecay.value = this._fading ? FADE_DECAY : this._decay;
    this._feedbackUniforms.uSeed.value = (this._frameCount * 13.13) % 64;
    renderer.setRenderTarget(this._buffer.write);
    renderer.render(this._feedbackScene, this._ortho);

    this._frameCount++;
    if (!this._fading && this._frameCount % GOVERN_EVERY_FRAMES === 0) this._measureLuminance(renderer);

    renderer.setRenderTarget(prevTarget);
    renderer.autoClear = prevAutoClear;

    this._buffer.swap();
    this._displayUniforms.uBuffer.value = this._buffer.read.texture;

    if (this._lumEl && this._frameCount % 10 === 0) {
      this._lumEl.textContent = 'tessel  lum ' + this._lastLum.toFixed(3)
        + '\n        decay ' + this._decay.toFixed(3)
        + '\n        ' + this._group + '  cells ' + this._cells.toFixed(1)
        + (this._governorHot ? '\n        GOVERNOR ACTIVE' : '');
    }
  }

  // The fold softening is one BUFFER PIXEL expressed in cell units, so it is a
  // pixel at every tier and after every resize rather than a fixed fraction
  // that would be four pixels at quarter resolution.
  _updateSoft() {
    const px = 1 / Math.max(1, this._buffer.height);
    const aspect = this._feedbackUniforms.uAspect.value;
    this._feedbackUniforms.uSoft.value = Math.max(1e-4,
      px * this._feedbackUniforms.uCells.value / aspect);
  }

  _applyPalette(palette) {
    const vivid = (palette && palette.vividColors && palette.vividColors.length)
      ? palette.vividColors : [0x39a0ff, 0xff3ba7, 0xffd23b];
    for (let i = 0; i < 3; i++) this._palette[i].set(vivid[i % vivid.length]);
    const hsl = { h: 0, s: 0, l: 0 };
    this._palette[0].getHSL(hsl);
    this._hueDir = hsl.h >= 0.5 ? -1 : 1;
  }

  _setGroup(name) {
    this._group = name;
    this._feedbackUniforms.uGroup.value = GROUP_ID[name];
  }

  _setCells(n) {
    this._cells = n;
    this._feedbackUniforms.uCells.value = n;
    this._updateSoft();
  }

  // The slide: a quarter cell per bar, glided over a beat, in a direction that
  // turns by the group's own angle so the motion runs along a lattice
  // direction rather than across the mirrors.
  _stepSlide(spb) {
    const turn = (SLIDE_TURN[this._group] || 90) * Math.PI / 180;
    this._slideDir += turn;
    const cur = this._feedbackUniforms.uOffset.value;
    this._offFrom.copy(cur);
    this._offTo.set(
      cur.x + Math.cos(this._slideDir) * SLIDE_PER_BAR,
      cur.y + Math.sin(this._slideDir) * SLIDE_PER_BAR);
    this._offT = 0;
    this._offDur = Math.max(0.12, SLIDE_GLIDE_BEATS * spb);
  }

  // Into the canonical cell - the one at the middle of the frame, whose
  // content is the matter the fold replicates into every other cell. Off its
  // centre, because a shape sitting on the cell's centre is a fixed point of
  // the fold and comes back trivially symmetric.
  _spawnShape() {
    const kind = Math.floor(Math.random() * 3);
    const color = this._palette[Math.floor(Math.random() * 3)];
    const mesh = kind === 0 ? this._bar : (kind === 1 ? this._ring : this._dot);
    for (const m of this._shapes) m.visible = false;
    mesh.visible = true;
    const cw = this._cellWidth();
    // Inside the FUNDAMENTAL DOMAIN, the way Kaleido puts its shapes inside
    // the wedge. The fold only reads the domain back, so a shape spawned
    // anywhere else is injected once and never replicated - and when there is
    // no cover it is the loop's only source, so the loop starves. Measured
    // without artwork: frame mean exactly 0 at six of nine samples over three
    // minutes, against 0.015-0.38 with a cover.
    const a = this._domainAngle(0.15 + Math.random() * 0.7);
    const rad = cw * (0.10 + Math.random() * 0.22);
    mesh.position.set(Math.cos(a) * rad, Math.sin(a) * rad, 0);
    mesh.scale.setScalar(cw * 1.5);
    mesh.rotation.z = Math.random() * Math.PI;
    mesh.material.uniforms.uColor.value.copy(color);
    mesh.material.uniforms.uOpacity.value = 1.0;
    this._shapeUntil = this._time + SHAPE_LIFE_S;
  }

  // An angle lying inside the group's fundamental domain, measured from the
  // cell centre. Every one of these is inside the first quadrant except cmm's,
  // whose rhombic domain straddles the x axis.
  _domainAngle(t) {
    if (this._group === 'p6m') return t * Math.PI / 6;                 // 0..30
    if (this._group === 'p3m1') return Math.PI / 6 + t * Math.PI / 3;  // 30..90
    if (this._group === 'cmm') return (t - 0.5) * Math.PI / 3;         // -30..30
    return t * Math.PI / 2;                                            // 0..90
  }

  _cellWidth() {
    const aspect = this._feedbackUniforms.uAspect.value;
    return aspect / Math.max(0.5, this._feedbackUniforms.uCells.value);
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;
    this._time += deltaTime;

    const director = profile.director;
    const bands = director && director.bands;
    const graded = !!(director && director.hasReliableGrid) && bands;
    const low = graded && bands.low != null ? clamp01(bands.low) : 0.5;
    const mid = graded && bands.mid != null ? clamp01(bands.mid) : 0.5;
    const high = graded && bands.high != null ? clamp01(bands.high) : 0.5;
    const k = 1 - Math.exp(-deltaTime / BAND_TAU);
    this._lowSmooth += (low - this._lowSmooth) * k;
    this._midSmooth += (mid - this._midSmooth) * k;
    this._highSmooth += (high - this._highSmooth) * k;
    this._bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || null;
    const spb = this._bpm ? 60 / this._bpm : 0.5;

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
    }

    if (profile.trackId !== this._lastTrackId) {
      const first = this._lastTrackId === undefined;
      this._lastTrackId = profile.trackId;
      if (!first) this._fadeUntil = this._time + TRACK_FADE_S;
    }
    const fading = this._time < this._fadeUntil;
    this._fading = fading;

    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      if (profile.artworkUrl) {
        const self = this;
        loadArtworkTexture(profile.artworkUrl, { wrap: THREE.ClampToEdgeWrapping, mipmaps: false },
          function (tex) {
            if (self._artTexture) self._artTexture.dispose();
            self._artTexture = tex;
            self._artMesh.material.map = tex;
            self._artMesh.material.needsUpdate = true;
            self._artMesh.visible = true;
          },
          function () {});
      } else {
        this._artMesh.visible = false;
      }
    }

    // --- drops: the whole screen becomes ONE folded cell ---------------------
    const nextEvent = director && director.nextEvent;
    if (nextEvent && (nextEvent.type === 'drop' || nextEvent.type === 'rise')
      && this._dropUntil < this._time && this._forcedCells == null) {
      const until = nextEvent.msUntil / 1000;
      if (until > 0 && until <= 0.12) {
        this._cellsBefore = this._cells;
        this._setCells(DROP_CELLS);
        this._dropUntil = this._time + 2 * spb;
      }
    }
    const dropping = this._time < this._dropUntil;
    if (!dropping && this._cellsBefore != null) {
      this._setCells(this._cellsBefore);
      this._cellsBefore = null;
    }

    // --- musical events -------------------------------------------------------
    const beatIndex = profile.beatIndex;
    if (this._coldStart && !fading && this._time >= this._nextSeedAt) {
      this._nextSeedAt = this._time + 0.7;
      this._spawnShape();
    }
    if (beatIndex != null && this._lastBeatIndex != null && beatIndex !== this._lastBeatIndex && !fading) {
      this._coldStart = false;
      this._spawnShape();
      const barIndex = profile.barIndex;
      if (barIndex != null && barIndex !== this._lastBarIndex) {
        this._reseedUntil = this._time + RESEED_LIFE_S;
        this._stepSlide(spb);
      }
      this._lastBarIndex = profile.barIndex;
    } else if (this._lastBarIndex === null) {
      this._lastBarIndex = profile.barIndex;
    }
    this._lastBeatIndex = beatIndex;

    // --- phrases: a new group, a new cell count, a notch of rotation ---------
    const phraseIndex = profile.phraseIndex;
    if (!dropping && phraseIndex != null && this._lastPhraseIndex != null
      && phraseIndex !== this._lastPhraseIndex) {
      const progress = profile.duration > 0 ? clamp01(profile.progress)
        : (director && director.intensity != null ? director.intensity : 0.5);
      const mid2 = progress > 0.2 && progress < 0.8;
      if (this._forcedGroup == null) {
        const pool = this._layout.groups.filter((g) => mid2 || CALM_GROUPS.indexOf(g) !== -1);
        const pick = (pool.length ? pool : this._layout.groups).filter((g) => g !== this._group);
        if (pick.length) this._setGroup(pick[Math.floor(Math.random() * pick.length)]);
      }
      if (this._forcedCells == null) {
        const pick = CELL_COUNTS.filter((n) => n !== Math.round(this._cells));
        this._setCells(pick[Math.floor(Math.random() * pick.length)]);
      }
      // A notch of rotation. On p4m a 90 degree notch is INVISIBLE - the group
      // maps onto itself under it - so the table gives 45, which does show.
      this._rotation += (ROT_STEP_DEG[this._group] || 90) * Math.PI / 180;
    }
    this._lastPhraseIndex = phraseIndex;

    // The shape's flash is over - but with no cover it is the loop's ONLY
    // source, and a source that is absent for seven eighths of every beat
    // leaves the loop marginally sub-critical: the floor subtraction wins
    // between flashes, and zero is an absorbing state, so the frame goes
    // black and can only be restarted by the next flash landing hard enough.
    // Measured without artwork: black at three of nine samples over three
    // minutes even with the shapes inside the domain. With a cover the cover
    // is the continuous source and this never arises, so the hold-over only
    // applies when there is no artwork.
    if (this._time >= this._shapeUntil) {
      const hold = !this._artTexture && !fading;
      for (const m of this._shapes) {
        if (hold && m.visible) m.material.uniforms.uOpacity.value = SHAPE_HOLD_OPACITY;
        else m.visible = false;
      }
    }
    const reseeding = this._time < this._reseedUntil;
    this._artMesh.visible = !!this._artTexture && !fading;
    this._artMesh.material.opacity = reseeding ? ART_RESEED_OPACITY : ART_OPACITY;
    if (fading) { for (const m of this._shapes) m.visible = false; }

    // --- the transforms -------------------------------------------------------
    // The cells BREATHE on the beat. beatPhase drives it directly rather than a
    // free-running oscillator, so the breath cannot drift off the grid.
    const bp = profile.beatPhase != null ? profile.beatPhase : (this._time / spb) % 1;
    this._beatPhase = bp;
    const base = dropping ? DROP_CELLS : (this._cellsBefore != null ? this._cellsBefore : this._cells);
    const dig = Math.exp(-bp * CELL_BREATH_DECAY);
    const amp = CELL_BREATH * (0.5 + this._lowSmooth);
    this._feedbackUniforms.uCells.value = base * (1.0 + amp * (dig * 2.0 - 1.0));
    this._updateSoft();

    // The SLIDE. bands.mid moves it along faster within its glide.
    if (this._offT < 1) {
      this._offT = Math.min(1, this._offT + deltaTime / this._offDur * (0.7 + 0.6 * this._midSmooth));
      const t = this._offT * this._offT * (3 - 2 * this._offT);
      this._feedbackUniforms.uOffset.value.set(
        lerp(this._offFrom.x, this._offTo.x, t),
        lerp(this._offFrom.y, this._offTo.y, t));
    }

    this._feedbackUniforms.uRotation.value = this._rotation;
    this._feedbackUniforms.uZoom.value = ZOOM_PER_CELL;

    // The cover fills the canonical cell, off its centre so the fold does not
    // map it onto itself.
    const cw = this._cellWidth();
    this._artMesh.scale.set(ART_SIZE * cw, ART_SIZE * cw, 1);
    this._artMesh.position.set(cw * 0.18, cw * 0.12, 0);

    // Hue drift. Because the tiling slides, this lays down diagonal bands
    // across the frame rather than Kaleido's concentric rings.
    hueMatrix(HUE_SHIFT * (0.6 + 0.8 * this._highSmooth) * Math.PI * 2 * this._hueDir,
      this._feedbackUniforms.uHueMat.value);
  }

  dispose() {
    this._scene.remove(this._displayMesh);
    this._displayMesh.geometry.dispose();
    this._displayMesh.material.dispose();
    this._feedbackQuad.geometry.dispose();
    this._feedbackQuad.material.dispose();
    for (const m of this._shapes.concat([this._artMesh])) {
      m.geometry.dispose();
      m.material.dispose();
    }
    this._shapeMaterials.length = 0;
    this._lumQuad.geometry.dispose();
    this._lumQuad.material.dispose();
    this._lumTarget.dispose();
    this._buffer.dispose();
    this._source.dispose();
    if (this._artTexture) this._artTexture.dispose();
    if (this._lumEl && this._lumEl.parentNode) this._lumEl.parentNode.removeChild(this._lumEl);
  }
}
